// src/components/article/OpenArticleAnchor.tsx
'use client';

import { useRouter } from 'next/navigation';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';

function shouldUseClientNav(e: MouseEvent<HTMLAnchorElement>) {
  if (e.defaultPrevented) return false;
  if (e.button !== 0) return false; // 只攔截左鍵
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false; // 保留開新分頁等
  const target = (e.currentTarget.getAttribute('target') || '').toLowerCase();
  if (target && target !== '_self') return false;
  return true;
}

type Props = {
  /**
   * 文章 ID
   * 新版 normalizeArticle 已改為：`${table}_${table_id}`
   * 例如：collection_682、store_article_411
   * href 會變成 `/article/${id}` → `/article/collection_682`
   */
  id: string;
  /** 導覽前要先做的事（例如關掉浮層、暫停影片…） */
  beforeNavigate?: () => void;
  className?: string;
  children: React.ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>;

export default function OpenArticleAnchor({
  id,
  className,
  children,
  beforeNavigate,
  ...rest
}: Props) {
  const router = useRouter();
  const href = `/article/${id}`;

  return (
    <a
      href={href}
      className={className}
      data-opener={`article-${id}`} // 讓 @modal 關閉時能回焦
      {...rest}
      onClick={(e) => {
        if (!shouldUseClientNav(e)) return; // 讓瀏覽器原生處理（新分頁/新視窗）
        e.preventDefault();
        try {
          beforeNavigate?.();
        } finally {
          // 這裡用 router.push 讓 /article/[id] 串接 @modal route
          router.push(href, { scroll: false });
        }
      }}
    >
      {children}
    </a>
  );
}
